import { ActuatorChannelConnectionI, ActuatorStateI } from '../redux/types/app';

export const APPLIANCES_PER_CHANNEL = 100;

const toUniqueIds = (ids: number[] = []) => Array.from(new Set(ids));

export const getActuatorState = (
    hasAllApplianceChannelsUsed: boolean,
    hasControllerOrDetectorConnection: boolean
): 'ok' | 'warning' | 'error' => {
    if (hasAllApplianceChannelsUsed && hasControllerOrDetectorConnection) {
        return 'ok';
    }

    if (hasAllApplianceChannelsUsed || hasControllerOrDetectorConnection) {
        return 'warning';
    }

    return 'error';
};

export const getActuatorNumberOfChannels = (actuator?: ActuatorStateI): number => {
    const legacyChannels = actuator?.maximumApplianceConnections;
    const rawChannels = actuator?.numberOfChannels ?? legacyChannels ?? 1;
    return Math.max(1, Math.floor(rawChannels));
};

export const getApplianceCapacityPerChannel = (actuator?: ActuatorStateI): number => {
    const configuredCapacity = actuator?.maximumAppliancesPerChannel;
    if (configuredCapacity === undefined) {
        return APPLIANCES_PER_CHANNEL;
    }

    return Math.max(1, Math.floor(configuredCapacity));
};

export const normalizeActuatorChannelConnections = (
    channels: ActuatorChannelConnectionI[] | undefined,
    numberOfChannels: number,
    fallbackConnectedAppliances: number[] = [],
    applianceCapacityPerChannel = APPLIANCES_PER_CHANNEL
): ActuatorChannelConnectionI[] => {
    if (channels && channels.length > 0) {
        const normalized = channels
            .map((channel) => ({
                channel: Math.max(1, Math.floor(channel.channel)),
                applianceIds: toUniqueIds(channel.applianceIds ?? []).slice(0, applianceCapacityPerChannel)
            }))
            .filter((channel) => channel.channel <= numberOfChannels)
            .sort((a, b) => a.channel - b.channel);

        if (normalized.length > 0) {
            return normalized;
        }
    }

    if (fallbackConnectedAppliances.length === 0) {
        return [];
    }

    const uniqueIds = toUniqueIds(fallbackConnectedAppliances);
    const nextChannels: ActuatorChannelConnectionI[] = [];

    for (let index = 0; index < uniqueIds.length; index += applianceCapacityPerChannel) {
        const channelNumber = Math.floor(index / applianceCapacityPerChannel) + 1;
        if (channelNumber > numberOfChannels) {
            break;
        }

        nextChannels.push({
            channel: channelNumber,
            applianceIds: uniqueIds.slice(index, index + applianceCapacityPerChannel)
        });
    }

    return nextChannels;
};

export const getConnectedApplianceIds = (actuator?: ActuatorStateI): number[] => {
    if (!actuator) {
        return [] as number[];
    }

    const numberOfChannels = getActuatorNumberOfChannels(actuator);
    const normalizedChannels = normalizeActuatorChannelConnections(
        actuator.connectedAppliancesByChannel,
        numberOfChannels,
        actuator.connectedAppliances ?? [],
        getApplianceCapacityPerChannel(actuator)
    );

    return toUniqueIds(normalizedChannels.flatMap((channel) => channel.applianceIds));
};

export const isApplianceConnectedToActuator = (actuator: ActuatorStateI | undefined, applianceId: number): boolean => {
    return getConnectedApplianceIds(actuator).includes(applianceId);
};

export const getApplianceChannel = (actuator: ActuatorStateI | undefined, applianceId: number): number | undefined => {
    if (!actuator) {
        return undefined;
    }

    const numberOfChannels = getActuatorNumberOfChannels(actuator);
    const normalizedChannels = normalizeActuatorChannelConnections(
        actuator.connectedAppliancesByChannel,
        numberOfChannels,
        actuator.connectedAppliances ?? [],
        getApplianceCapacityPerChannel(actuator)
    );

    return normalizedChannels.find((channel) => channel.applianceIds.includes(applianceId))?.channel;
};

export const assignApplianceToActuator = (actuator: ActuatorStateI, applianceId: number): { actuator: ActuatorStateI; assigned: boolean; channel: number | undefined } => {
    const numberOfChannels = getActuatorNumberOfChannels(actuator);
    const normalizedChannels = normalizeActuatorChannelConnections(
        actuator.connectedAppliancesByChannel,
        numberOfChannels,
        actuator.connectedAppliances ?? [],
        getApplianceCapacityPerChannel(actuator)
    );

    if (normalizedChannels.some((channel) => channel.applianceIds.includes(applianceId))) {
        return {
            actuator: {
                ...actuator,
                numberOfChannels,
                connectedAppliancesByChannel: normalizedChannels,
                connectedAppliances: getConnectedApplianceIds({
                    ...actuator,
                    numberOfChannels,
                    connectedAppliancesByChannel: normalizedChannels
                })
            },
            assigned: true,
            channel: getApplianceChannel({ ...actuator, connectedAppliancesByChannel: normalizedChannels, numberOfChannels }, applianceId)
        };
    }

    const allChannels = Array.from({ length: numberOfChannels }, (_, index) => index + 1);
    const unusedChannel = allChannels.find((channel) => {
        const existing = normalizedChannels.find((item) => item.channel === channel);
        return existing === undefined || existing.applianceIds.length === 0;
    });

    const prioritizedChannels = unusedChannel !== undefined
        ? [unusedChannel, ...allChannels.filter((channel) => channel !== unusedChannel)]
        : [1, ...allChannels.filter((channel) => channel !== 1)];

    for (const channel of prioritizedChannels) {
        const existingIndex = normalizedChannels.findIndex((item) => item.channel === channel);
        const existing = existingIndex === -1
            ? { channel, applianceIds: [] as number[] }
            : normalizedChannels[existingIndex];

        if (existing.applianceIds.length >= getApplianceCapacityPerChannel(actuator)) {
            continue;
        }

        const updatedChannel = {
            ...existing,
            applianceIds: [...existing.applianceIds, applianceId]
        };

        const nextChannels = existingIndex === -1
            ? [...normalizedChannels, updatedChannel].sort((a, b) => a.channel - b.channel)
            : normalizedChannels.map((item, index) => index === existingIndex ? updatedChannel : item);

        return {
            actuator: {
                ...actuator,
                numberOfChannels,
                connectedAppliancesByChannel: nextChannels,
                connectedAppliances: toUniqueIds(nextChannels.flatMap((item) => item.applianceIds))
            },
            assigned: true,
            channel
        };
    }

    return {
        actuator: {
            ...actuator,
            numberOfChannels,
            connectedAppliancesByChannel: normalizedChannels,
            connectedAppliances: toUniqueIds(normalizedChannels.flatMap((item) => item.applianceIds))
        },
        assigned: false,
        channel: undefined as number | undefined
    };
};

export const removeApplianceFromActuator = (actuator: ActuatorStateI, applianceId: number): ActuatorStateI => {
    const numberOfChannels = getActuatorNumberOfChannels(actuator);
    const normalizedChannels = normalizeActuatorChannelConnections(
        actuator.connectedAppliancesByChannel,
        numberOfChannels,
        actuator.connectedAppliances ?? [],
        getApplianceCapacityPerChannel(actuator)
    );

    const nextChannels = normalizedChannels
        .map((channel) => ({
            ...channel,
            applianceIds: channel.applianceIds.filter((id) => id !== applianceId)
        }))
        .filter((channel) => channel.applianceIds.length > 0);

    return {
        ...actuator,
        numberOfChannels,
        connectedAppliancesByChannel: nextChannels,
        connectedAppliances: toUniqueIds(nextChannels.flatMap((item) => item.applianceIds))
    };
};

export const removeAppliancesFromActuator = (actuator: ActuatorStateI, applianceIds: Set<number>): ActuatorStateI => {
    const numberOfChannels = getActuatorNumberOfChannels(actuator);
    const normalizedChannels = normalizeActuatorChannelConnections(
        actuator.connectedAppliancesByChannel,
        numberOfChannels,
        actuator.connectedAppliances ?? [],
        getApplianceCapacityPerChannel(actuator)
    );

    const nextChannels = normalizedChannels
        .map((channel) => ({
            ...channel,
            applianceIds: channel.applianceIds.filter((id) => !applianceIds.has(id))
        }))
        .filter((channel) => channel.applianceIds.length > 0);

    return {
        ...actuator,
        numberOfChannels,
        connectedAppliancesByChannel: nextChannels,
        connectedAppliances: toUniqueIds(nextChannels.flatMap((item) => item.applianceIds))
    };
};

export const moveApplianceToActuatorChannel = (actuator: ActuatorStateI, applianceId: number, targetChannel: number): { actuator: ActuatorStateI; moved: boolean } => {
    const numberOfChannels = getActuatorNumberOfChannels(actuator);
    const normalizedTarget = Math.max(1, Math.min(numberOfChannels, Math.floor(targetChannel)));
    const normalizedChannels = normalizeActuatorChannelConnections(
        actuator.connectedAppliancesByChannel,
        numberOfChannels,
        actuator.connectedAppliances ?? [],
        getApplianceCapacityPerChannel(actuator)
    );

    const withoutAppliance = normalizedChannels
        .map((channel) => ({
            ...channel,
            applianceIds: channel.applianceIds.filter((id) => id !== applianceId)
        }))
        .filter((channel) => channel.applianceIds.length > 0);

    const targetIndex = withoutAppliance.findIndex((channel) => channel.channel === normalizedTarget);
    const currentTarget = targetIndex === -1
        ? { channel: normalizedTarget, applianceIds: [] as number[] }
        : withoutAppliance[targetIndex];

    if (currentTarget.applianceIds.length >= getApplianceCapacityPerChannel(actuator)) {
        return {
            actuator: {
                ...actuator,
                numberOfChannels,
                connectedAppliancesByChannel: withoutAppliance,
                connectedAppliances: toUniqueIds(withoutAppliance.flatMap((item) => item.applianceIds))
            },
            moved: false
        };
    }

    const updatedTarget = {
        ...currentTarget,
        applianceIds: [...currentTarget.applianceIds, applianceId]
    };

    const nextChannels = targetIndex === -1
        ? [...withoutAppliance, updatedTarget].sort((a, b) => a.channel - b.channel)
        : withoutAppliance.map((channel, index) => index === targetIndex ? updatedTarget : channel);

    return {
        actuator: {
            ...actuator,
            numberOfChannels,
            connectedAppliancesByChannel: nextChannels,
            connectedAppliances: toUniqueIds(nextChannels.flatMap((item) => item.applianceIds))
        },
        moved: true
    };
};

export const getUsedActuatorChannels = (actuator: ActuatorStateI | undefined): number => {
    if (!actuator) {
        return 0;
    }

    const numberOfChannels = getActuatorNumberOfChannels(actuator);
    const normalizedChannels = normalizeActuatorChannelConnections(
        actuator.connectedAppliancesByChannel,
        numberOfChannels,
        actuator.connectedAppliances ?? [],
        getApplianceCapacityPerChannel(actuator)
    );

    return normalizedChannels.filter((channel) => channel.applianceIds.length > 0).length;
};
